/**
 * Frontend service for the transfer-fulfillment Medplum Bot.
 *
 * Invoked when intake manually assigns a new provider during the transfer stage.
 * Performs the full provider handoff: EoC careManager, CareTeam, compartment
 * access, cross-practice performer, and SR stage → fulfilled.
 *
 * Bot identifier: { system: 'https://unify.health/bots', value: 'transfer-fulfillment' }
 */

import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Identifier, EpisodeOfCare, Practitioner } from '@medplum/fhirtypes';
import { getRole } from '@unify/fhir-model/resources/practitioner';

export const TRANSFER_FULFILLMENT_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'transfer-fulfillment',
} as const;

export function getTransferFulfillmentBotTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_TRANSFER_FULFILLMENT_BOT_ID?.trim();
  if (id) return id;
  const system =
    import.meta.env.VITE_MEDPLUM_TRANSFER_FULFILLMENT_BOT_IDENTIFIER_SYSTEM?.trim() ??
    TRANSFER_FULFILLMENT_BOT_IDENTIFIER.system;
  const value =
    import.meta.env.VITE_MEDPLUM_TRANSFER_FULFILLMENT_BOT_IDENTIFIER_VALUE?.trim() ??
    TRANSFER_FULFILLMENT_BOT_IDENTIFIER.value;
  return { system, value };
}

/** Roles eligible to receive a transfer (must have a panel CareTeam in pilot RBAC). */
const TRANSFER_RECEIVING_ROLES = new Set(['provider', 'supervisor']);

export function isTransferReceivingPractitioner(practitioner: Practitioner): boolean {
  const role = getRole(practitioner);
  return role != null && TRANSFER_RECEIVING_ROLES.has(role);
}

export function filterPractitionersForTransferFulfillment(
  practitioners: Practitioner[],
  opts?: { excludePractitionerIds?: readonly string[] },
): Array<Practitioner & { id: string }> {
  const excluded = new Set(opts?.excludePractitionerIds ?? []);
  return practitioners.filter(
    (p): p is Practitioner & { id: string } =>
      !!p.id && !excluded.has(p.id) && isTransferReceivingPractitioner(p),
  );
}

export function careManagerPractitionerIdFromEpisode(
  eoc: EpisodeOfCare,
): string | undefined {
  const ref = eoc.careManager?.reference;
  if (!ref?.startsWith('Practitioner/')) return undefined;
  return ref.slice('Practitioner/'.length);
}

/** Care managers on other active episodes for the same patient (exclude current EoC). */
export async function fetchOtherEpisodeCareManagerIds(
  medplum: MedplumClient,
  patientId: string,
  currentEpisodeId: string,
): Promise<string[]> {
  const eocs = await medplum.searchResources('EpisodeOfCare', {
    patient: `Patient/${patientId}`,
    _count: '50',
  });
  const ids = new Set<string>();
  for (const eoc of eocs) {
    if (!eoc.id || eoc.id === currentEpisodeId) continue;
    const cmId = careManagerPractitionerIdFromEpisode(eoc);
    if (cmId) ids.add(cmId);
  }
  return [...ids];
}

export interface TransferFulfillmentInput {
  serviceRequestId: string;
  receivingPractitionerId: string;
}

export interface TransferFulfillmentResult {
  success: boolean;
  serviceRequestId: string;
  stage: string;
  careTeamId?: string;
  receivingPractitionerId: string;
}

export async function executeTransferFulfillment(
  medplum: MedplumClient,
  input: TransferFulfillmentInput,
): Promise<TransferFulfillmentResult> {
  return medplum.executeBot(
    getTransferFulfillmentBotTarget(),
    input,
    ContentType.JSON,
  ) as Promise<TransferFulfillmentResult>;
}
